import { Loading, TitleBar } from "modules/common";
import { ToggleSwitch } from "modules/common/components/ToggleSwitch/ToggleSwitch";
import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import {
    useGetUserStationPreferences,
    useUpdateUserStationPreferences,
} from "services/account/stationPreferencesHooks";
import { UserStationPreferences } from "services/account/stationPreferencesTypes";
import { RootState } from "../../../../store";
import "./StationPreferencesSettings.css";
import { isCommunicatorAdviser, isEquipmentAdmin, isMinistryAdviser, isSuperAdmin } from "../../../auth";


interface StationPreferencesProps {
    className?: string;
    enabled?: boolean;
}

export const StationPreferencesSettings: React.FC<StationPreferencesProps> = ({
    className,
    enabled = true,
}) => {
    const { data: preferencesData, isLoading } = useGetUserStationPreferences(enabled);
    const updatePreferencesMutation = useUpdateUserStationPreferences();
    const { profile } = useSelector((state: RootState) => state.profile);
    const roleType = profile?.roleType?.value;
    const hasAgrosenzorikaAccess =
        isMinistryAdviser(roleType) ||
        isSuperAdmin(roleType) ||
        isCommunicatorAdviser(roleType) ||
        isEquipmentAdmin(roleType);

    const [preferences, setPreferences] = useState<UserStationPreferences | null>(null);

    useEffect(() => {
        if (
            preferencesData?.stationPreferences &&
            (preferencesData.success === true || preferencesData.success === undefined)
        ) {
            setPreferences(preferencesData.stationPreferences);
        }
    }, [preferencesData]);

    if (isLoading || !preferences) {
        return <Loading />;
    }

    const handleToggle = (key: keyof UserStationPreferences) => {
        let updatedPreferences = {
            ...preferences,
            [key]: !preferences[key],
        };

        if (key === "showMainReadings" && preferences[key] === true) {
            updatedPreferences = {
                ...updatedPreferences,
                showWindDirectionAndSpeed: false,
                showTemperature: false,
                showAirHumidity: false,
            };
        }

        setPreferences(updatedPreferences);

        updatePreferencesMutation.mutate({
            stationPreferences: updatedPreferences,
        });
    };

    const handleSwitchChange =
        (key: keyof UserStationPreferences) => (event: React.ChangeEvent<HTMLInputElement>) => {
            handleToggle(key);
        };

    return (
        <div className={className}>
            <TitleBar>
                <TitleBar.Left>
                    <span className="title-bar-text">Postavke prikaza stanica</span>
                </TitleBar.Left>
                <TitleBar.Right>
                    <span className="title-bar-description">
                        Podešavanje prikaza različitih tipova stanica i njihovih detalja
                    </span>
                </TitleBar.Right>
            </TitleBar>

            <div className="settings-section">
                {hasAgrosenzorikaAccess && (
                    <div className="settings-group">
                        <div className="settings-group-header">
                            <h3 className="settings-group-title">AGROSENZORIKA</h3>
                        </div>

                        <div className="settings-items">
                            <div className="settings-item">
                                <label className="settings-label">Lovke hranidbene</label>
                                <ToggleSwitch
                                    checked={preferences.showFoodTraps}
                                    onChange={handleSwitchChange("showFoodTraps")}
                                    htmlFor="showFoodTraps"
                                    isDisabled={updatePreferencesMutation.isPending}
                                />
                            </div>

                            <div className="settings-item">
                                <label className="settings-label">Lovke feromonske</label>
                                <ToggleSwitch
                                    checked={preferences.showPheromoneTraps}
                                    onChange={handleSwitchChange("showPheromoneTraps")}
                                    htmlFor="showPheromoneTraps"
                                    isDisabled={updatePreferencesMutation.isPending}
                                />
                            </div>

                            <div className="settings-item">
                                <label className="settings-label">Lovke, ljepljive trake</label>
                                <ToggleSwitch
                                    checked={preferences.showStickyTraps}
                                    onChange={handleSwitchChange("showStickyTraps")}
                                    htmlFor="showStickyTraps"
                                    isDisabled={updatePreferencesMutation.isPending}
                                />
                            </div>

                            <div className="settings-item">
                                <label className="settings-label">Kamere fenofaze</label>
                                <ToggleSwitch
                                    checked={preferences.showPhenophaseCameras}
                                    onChange={handleSwitchChange("showPhenophaseCameras")}
                                    htmlFor="showPhenophaseCameras"
                                    isDisabled={updatePreferencesMutation.isPending}
                                />
                            </div>

                            <div className="settings-item">
                                <label className="settings-label">Agrometeorološke stanice</label>
                                <ToggleSwitch
                                    checked={preferences.showAgrometeorologicalStations}
                                    onChange={handleSwitchChange("showAgrometeorologicalStations")}
                                    htmlFor="showAgrometeorologicalStations"
                                    isDisabled={updatePreferencesMutation.isPending}
                                />
                            </div>
                        </div>
                    </div>
                )}

                <div className="settings-group">
                    <div className="settings-group-header">
                        <h3 className="settings-group-title">POSTAVKE PRIKAZA DETALJA UREĐAJA</h3>
                    </div>

                    <div className="settings-items">
                        <div className="settings-item">
                            <label className="settings-label">Položaj uređaja</label>
                            <ToggleSwitch
                                checked={preferences.showDevicePosition}
                                onChange={handleSwitchChange("showDevicePosition")}
                                htmlFor="showDevicePosition"
                                isDisabled={updatePreferencesMutation.isPending}
                            />
                        </div>

                        <div className="settings-item">
                            <label className="settings-label">Glavna očitanja</label>
                            <ToggleSwitch
                                checked={preferences.showMainReadings}
                                onChange={handleSwitchChange("showMainReadings")}
                                htmlFor="showMainReadings"
                                isDisabled={updatePreferencesMutation.isPending}
                            />
                        </div>

                        <div className="settings-item">
                            <label className="settings-label">Smjer i brzina vjetra</label>
                            <ToggleSwitch
                                checked={preferences.showWindDirectionAndSpeed}
                                onChange={handleSwitchChange("showWindDirectionAndSpeed")}
                                htmlFor="showWindDirectionAndSpeed"
                                isDisabled={
                                    updatePreferencesMutation.isPending ||
                                    !preferences.showMainReadings
                                }
                            />
                        </div>

                        <div className="settings-item">
                            <label className="settings-label">Temperatura</label>
                            <ToggleSwitch
                                checked={preferences.showTemperature}
                                onChange={handleSwitchChange("showTemperature")}
                                htmlFor="showTemperature"
                                isDisabled={
                                    updatePreferencesMutation.isPending ||
                                    !preferences.showMainReadings
                                }
                            />
                        </div>

                        <div className="settings-item">
                            <label className="settings-label">Vlaga zraka</label>
                            <ToggleSwitch
                                checked={preferences.showAirHumidity}
                                onChange={handleSwitchChange("showAirHumidity")}
                                htmlFor="showAirHumidity"
                                isDisabled={
                                    updatePreferencesMutation.isPending ||
                                    !preferences.showMainReadings
                                }
                            />
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};
